import * as THREE from 'three';
import {
  ResourceTier,
  RESOURCE_INFO,
  lookupWeapon,
  lookupShield,
  lookupConsumable,
} from '@viral-space/shared';
import {
  buildResourceMesh,
  buildWeaponMesh,
  buildShieldMesh,
  buildEngineMesh,
  buildConsumableMesh,
  disposeBuiltItem,
  type BuiltItem,
  type ConsumableEffectKind,
} from '../game/item-builder';

/**
 * Inventory / shop / drop thumbnails.
 *
 * The inventory and dock UIs can show 50+ items at once; spinning up a
 * `WebGLRenderer` per row would blow past the browser's WebGL context
 * limit (~16) and cause every thumbnail to flash to "context lost"
 * (this is exactly what happened in the admin gallery before we
 * switched to a shared renderer — see `admin-ship-preview.ts`).
 *
 * So this module owns **one** offscreen WebGL renderer, draws each
 * requested item into it, then `drawImage`s the result into the
 * caller's 2D canvas. The 2D canvas only holds a bitmap, not a WebGL
 * context, so we can fill the inventory grid with hundreds of cells
 * without losing contexts.
 */

const THUMB_SIZE = 96;

let _renderer: THREE.WebGLRenderer | null = null;
let _scene: THREE.Scene | null = null;
let _camera: THREE.PerspectiveCamera | null = null;
let _current: BuiltItem | null = null;

function ensureRenderer(): void {
  if (_renderer) return;
  const off = document.createElement('canvas');
  off.width = THUMB_SIZE;
  off.height = THUMB_SIZE;
  _renderer = new THREE.WebGLRenderer({
    canvas: off,
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true,
    powerPreference: 'low-power',
  });
  _renderer.setClearColor(0x000000, 0);
  _renderer.setPixelRatio(1);

  _scene = new THREE.Scene();
  _camera = new THREE.PerspectiveCamera(36, 1, 0.1, 100);
  _camera.position.set(1.2, 1.0, 2.4);
  _camera.lookAt(0, 0, 0);

  _scene.add(new THREE.AmbientLight(0x445566, 0.85));
  const key = new THREE.DirectionalLight(0xffffff, 1.2);
  key.position.set(3, 4, 5);
  _scene.add(key);
  const rim = new THREE.DirectionalLight(0x66aaff, 0.55);
  rim.position.set(-4, -1, -3);
  _scene.add(rim);
  const warm = new THREE.PointLight(0xffaa66, 0.5, 8, 2);
  warm.position.set(-1.5, 0.5, 2);
  _scene.add(warm);
}

function clearCurrent(): void {
  if (!_current || !_scene) return;
  _scene.remove(_current.group);
  disposeBuiltItem(_current);
  _current = null;
}

interface RenderOptions {
  /** Euler rotation applied to the built item before rendering. */
  rotation?: [number, number, number];
  /** Multiplier for emissive pulses (defaults to 1.3 — bright but not blown out). */
  pulseIntensity?: number;
}

/**
 * Internal helper: render `builtItem` to the shared offscreen canvas,
 * blit to the target 2D canvas, then dispose the built item.
 */
function renderBuiltToCanvas(
  targetCanvas: HTMLCanvasElement,
  built: BuiltItem,
  opts: RenderOptions = {},
): void {
  ensureRenderer();
  if (!_renderer || !_scene || !_camera) return;

  if (
    _renderer.domElement.width !== THUMB_SIZE ||
    _renderer.domElement.height !== THUMB_SIZE
  ) {
    _renderer.setSize(THUMB_SIZE, THUMB_SIZE, false);
    _camera.aspect = 1;
    _camera.updateProjectionMatrix();
  }

  clearCurrent();
  _current = built;

  const r = opts.rotation ?? [0.32, -0.55, 0];
  built.group.rotation.set(r[0], r[1], r[2]);

  const intensity = opts.pulseIntensity ?? 1.3;
  if (built.pulses) {
    for (const m of built.pulses) m.emissiveIntensity = intensity;
  }
  _scene.add(built.group);
  _renderer.render(_scene, _camera);

  if (
    targetCanvas.width !== THUMB_SIZE ||
    targetCanvas.height !== THUMB_SIZE
  ) {
    targetCanvas.width = THUMB_SIZE;
    targetCanvas.height = THUMB_SIZE;
  }
  const ctx = targetCanvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, THUMB_SIZE, THUMB_SIZE);
  ctx.drawImage(_renderer.domElement, 0, 0, THUMB_SIZE, THUMB_SIZE);

  clearCurrent();
}

/**
 * Render a static thumbnail of the given resource tier into the target
 * 2D canvas. Reuses one shared WebGL context across all calls.
 */
export function renderResourceThumbnail(
  targetCanvas: HTMLCanvasElement,
  tier: ResourceTier,
): void {
  const info = RESOURCE_INFO[tier];
  const color = info?.color ?? 0xffffff;
  renderBuiltToCanvas(targetCanvas, buildResourceMesh(tier, color));
}

/**
 * Render a static thumbnail of the given weapon id (e.g. "pulse_blaster").
 * Falls back to the default blaster geometry if the id is unknown.
 */
export function renderWeaponThumbnail(
  targetCanvas: HTMLCanvasElement,
  weaponId: string,
): void {
  const def = lookupWeapon(weaponId);
  const visualStyle = def?.visualStyle ?? 'blaster';
  const color = def?.projectileColor ?? 0xff8844;
  renderBuiltToCanvas(targetCanvas, buildWeaponMesh(visualStyle, color), {
    // Weapons read as silhouettes; a 3/4 down-and-front view is most
    // recognisable. Slight Y rotation pulls the muzzle toward camera.
    rotation: [0.18, -0.7, 0],
  });
}

/**
 * Render a static thumbnail of the given shield id.
 */
export function renderShieldThumbnail(
  targetCanvas: HTMLCanvasElement,
  shieldId: string,
): void {
  const def = lookupShield(shieldId);
  const visualStyle = def?.visualStyle ?? 'energy';
  const color = def?.visualColor ?? 0x44aaff;
  renderBuiltToCanvas(targetCanvas, buildShieldMesh(visualStyle, color), {
    rotation: [0.15, -0.45, 0],
  });
}

/**
 * Render a static thumbnail of the given engine id.
 */
export function renderEngineThumbnail(
  targetCanvas: HTMLCanvasElement,
  engineId: string,
): void {
  renderBuiltToCanvas(targetCanvas, buildEngineMesh(engineId), {
    // Engines look best with the nozzle aperture pointed slightly
    // toward the camera so the glowing throat is visible.
    rotation: [0.18, -0.85, 0],
  });
}

const CONSUMABLE_EFFECT_FOR_ID: Record<string, ConsumableEffectKind> = {
  // Repair kits
  repair_kit_small: 'repairHull',
  repair_kit_medium: 'repairHull',
  repair_kit_large: 'repairHull',
  repair_kit_epic: 'repairHull',
  sylphari_regen_pod: 'repairHull',
  // Shield batteries
  shield_battery_small: 'repairShield',
  shield_battery_medium: 'repairShield',
  shield_battery_large: 'repairShield',
  // Boosts (thrust)
  thrust_booster_short: 'boostThrust',
  thrust_booster_long: 'boostThrust',
  chronos_chrono_lens: 'boostThrust',
  // Boosts (damage)
  damage_booster_short: 'boostDamage',
  damage_booster_long: 'boostDamage',
  nova_combat_stim: 'boostDamage',
};

/**
 * Render a static thumbnail of the given consumable id.
 */
export function renderConsumableThumbnail(
  targetCanvas: HTMLCanvasElement,
  consumableId: string,
): void {
  // Prefer the canonical effect from the consumable definition; fall
  // back to a string-id mapping for ids the consumable registry doesn't
  // know about (e.g. legacy ids stored in old saves).
  const def = lookupConsumable(consumableId);
  const effect: ConsumableEffectKind = (def?.effect.kind as ConsumableEffectKind | undefined)
    ?? CONSUMABLE_EFFECT_FOR_ID[consumableId]
    ?? 'repairHull';
  renderBuiltToCanvas(targetCanvas, buildConsumableMesh(effect), {
    // A small pitch makes the cap/top legible without obscuring the
    // body; vials read better with a near-side-on framing than a 3/4.
    rotation: [0.05, -0.55, 0],
  });
}

/**
 * Free the shared offscreen renderer. Useful in unit tests and during
 * full-page teardown; production code generally lets it live for the
 * lifetime of the page.
 */
export function disposeItemThumbnailRenderer(): void {
  clearCurrent();
  if (_renderer) {
    _renderer.dispose();
    _renderer = null;
  }
  _scene = null;
  _camera = null;
}
